/**
 * The browser half of HMRC's fraud-prevention headers (DECISIONS-3-v7 §G5.2, §G5.3; T15 D4).
 *
 * Six values only a browser can originate, collected here and POSTed to
 * `submit_hmrc_fraud_headers` BEFORE the operator follows the authorize URL. This module never talks to
 * the network: `web/src/api/client.ts` is the single fetch site in the bundle (1d §B1.1) and it is what
 * attaches the session cookie and the `X-CSRF-Token` header.
 *
 * **`Gov-Client-Multi-Factor` is not collected here, and that is deliberate** (T15 D4, xmg-sec-mfa). Every
 * signed-in LuciMigration user passes a real second factor (TOTP) at sign-in, and the SERVER records that event
 * on the session. `submit_hmrc_fraud_headers` seals it into the attestation from the session row - type, the
 * moment the factor was passed, a hashed factor reference - so the value HMRC receives is the server's record,
 * never something the browser asserts. There is no collector for it below, so if a server ever asked the
 * browser for it (an old deployment, a misconfigured `required_headers`), `collectFraudSignals` throws and the
 * flow stops (W2) rather than sending a browser-made value.
 *
 * **The required list comes from the server**, in `ConsentStart.required_headers`, which the API builds
 * from `connectors.hmrc.fph.BROWSER_HEADERS`. This module holds a function per name - a
 * `Gov-Client-Screens` string cannot be produced generically - but it never holds its own idea of WHICH
 * names are required. A second list is how `Gov-Client-Browser-Plugins` went missing (§G5.2).
 *
 * **Every pair value is `encodeURIComponent`-encoded**, because the server side renders its own pair
 * headers with `quote(..., safe="")` (`connectors/hmrc/fph.py::_pairs`) and HMRC parses one format.
 */
import { client, unwrap } from "./client";

/** The six browser-derived values, keyed by HMRC's own header names. */
export type FraudSignals = Record<string, string>;

/**
 * **The typed shim for the three HMRC paths, and why it is a shim and not a `@ts-expect-error`**
 * (round-3 **B-18**).
 *
 * `client` is `createClient<paths>`, so its `GET`/`POST` accept only a `PathsWithMethod<paths, …>` —
 * and `grep -c 'connections/hmrc' web/src/api/schema.d.ts` is **0** until **task 20** regenerates the
 * contract and the client types with the mount (hand-off 3). Executed, the two calls below are
 * `TS2345: Argument of type '"/api/v1/connections/hmrc/fraud-headers"' is not assignable to parameter
 * of type 'PathsWithMethod<paths, "post">'`, and `npm run typecheck` (`tsc --noEmit`, `package.json`'s
 * `"typecheck"` script) runs in three of this task's gates.
 *
 * `@ts-expect-error` would close it today and **red the build the day task 20 lands**: the directive is
 * itself an error when the line beneath it has no error, so the suppression would have to be removed in
 * task 20's commit — a file this task owns, edited by task 20, which §J forbids. The shim expresses the
 * same window and survives the regeneration untouched: it is one cast of ONE object, in the one module
 * that already exists to hold both untyped paths (Part C2's reason for `startHmrcConsent` living here),
 * and it keeps the bodies and the response types checked.
 *
 * `hmrcPaths` is the SAME object as `client`, so the transport is unchanged — `client.ts` still attaches
 * the session cookie and the `X-CSRF-Token` header — and `vi.spyOn(client, "POST")` still observes the
 * call, because the method is read off that object at call time.
 */
type HmrcFetchResult<T> = { data?: T; error?: unknown; response: Response };

type HmrcPaths = {
  GET: <T>(
    path: "/api/v1/connections/hmrc/consent",
    init: { params: { query: Record<string, string> } },
  ) => Promise<HmrcFetchResult<T>>;
  POST: <T>(
    path: "/api/v1/connections/hmrc/fraud-headers",
    init: { body: { state: string; values: FraudSignals } },
  ) => Promise<HmrcFetchResult<T>>;
};

const hmrcPaths = client as unknown as HmrcPaths;

/** `key=value&key=value`, percent-encoded, in the order given - HMRC's pair-header format. */
function pairs(entries: readonly (readonly [string, string])[]): string {
  return entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join("&");
}

/**
 * A stable per-browser UUID. Persisted, because HMRC asks for a device identity, not a session one.
 *
 * **Every storage access is wrapped, which is the landed convention rather than defensiveness** —
 * `web/src/screens/connect/newMigrationDraft.ts`'s `readDraft`/`saveDraft`/`clearDraft` each wrap their
 * one `window.sessionStorage` call for the same reason its comment gives: *"A private window, blocked
 * site data or a corrupt value"*. Executed (round-3 Tier 0), the unwrapped version was not merely
 * defensive-in-theory: under the repo's own vitest environment `window.localStorage` is `undefined`, so
 * `window.localStorage.getItem(KEY)` threw `TypeError: Cannot read properties of undefined (reading
 * 'getItem')` and took THREE of this task's web tests with it — two in
 * `hmrcFraudSignals.test.ts` and `hmrcConsent.test.tsx`'s *"collects the six, posts them bound to the
 * state"*, because `collectFraudSignals` calls this first of the six.
 *
 * A device id we cannot persist is still a truthful device id for this session, so the fallback is a
 * fresh UUID rather than a throw: §G5.4's rule is that we never INVENT an attestation, and a
 * `Gov-Client-Device-ID` generated on this device is not an invention. What it costs is stability
 * across reloads in a browser that refuses storage, which HMRC's own guidance treats as acceptable and
 * which is strictly better than refusing the connection.
 */
function deviceId(): string {
  const KEY = "lucimig.hmrc.device-id";
  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing !== null) {
      return existing;
    }
    const minted = crypto.randomUUID();
    window.localStorage.setItem(KEY, minted);
    return minted;
  } catch {
    return crypto.randomUUID();
  }
}

export const COLLECTORS: Readonly<Record<string, () => string>> = {
  "Gov-Client-Device-ID": deviceId,
  "Gov-Client-Browser-JS-User-Agent": () => window.navigator.userAgent,
  "Gov-Client-Browser-Plugins": () =>
    Array.from(window.navigator.plugins ?? [])
      .map((plugin) => encodeURIComponent(plugin.name))
      .join(","),
  "Gov-Client-Timezone": () => {
    const offset = -new Date().getTimezoneOffset();
    const sign = offset < 0 ? "-" : "+";
    const abs = Math.abs(offset);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `UTC${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
  },
  "Gov-Client-Screens": () =>
    pairs([
      ["width", String(window.screen.width)],
      ["height", String(window.screen.height)],
      ["scaling-factor", String(window.devicePixelRatio || 1)],
      ["colour-depth", String(window.screen.colorDepth)],
    ]),
  "Gov-Client-Window-Size": () =>
    pairs([
      ["width", String(window.innerWidth)],
      ["height", String(window.innerHeight)],
    ]),
  // T15 D4: there is deliberately NO `Gov-Client-Multi-Factor` collector. LuciMigration has no operator
  // MFA of any kind, and `Gov-Client-Multi-Factor`'s entire meaning is evidence of a real, recent MFA
  // event - fabricating one (or replaying an old one) is exactly the invention §G5.4 makes impossible.
  // A server that still asks for it makes `collectFraudSignals` throw below (W2), rather than this
  // module inventing a value to satisfy it.
};

/**
 * Collect exactly the headers the server asked for, in the server's order.
 *
 * Throws on any name this module cannot produce, rather than returning a short map: §G5.2/decision 7 -
 * a connection carrying five of six fails HMRC's own validator on its first attended call, after the
 * customer has gone. The caller shows the error and does not follow the authorize URL.
 */
export function collectFraudSignals(required: readonly string[]): FraudSignals {
  const missing = required.filter((name) => !(name in COLLECTORS));
  if (missing.length > 0) {
    throw new Error(`cannot collect HMRC fraud-prevention headers: ${missing.join(", ")}`);
  }
  const out: FraudSignals = {};
  for (const name of required) {
    // The collector is looked up into a local and guarded, rather than called through
    // `COLLECTORS[name]()`: `tsconfig.json` sets `noUncheckedIndexedAccess`, so an index into a
    // `Record<string, …>` is `… | undefined` and the direct call is `TS2722: Cannot invoke an object
    // which is possibly 'undefined'` (executed, round-3 B-18). The `in` check above narrows nothing
    // across the loop boundary, and the guard is honest anyway - it is the same refusal.
    const collect = COLLECTORS[name];
    // A collector this module does not have is the failure (and the filter above has already thrown
    // for it); a collector that returns an EMPTY STRING is not. `Gov-Client-Browser-Plugins` is empty
    // in any browser with no plugins - and in the repo's jsdom environment - so refusing it refused
    // three of this task's own web tests and would refuse real devices (**R-17.10**). The server's
    // `FraudHeadersIn` and task 16's `fph.py::build_headers` both check PRESENCE for the same reason,
    // so all three sides now agree.
    const value = collect === undefined ? undefined : collect();
    if (value === undefined) {
      throw new Error(`cannot collect HMRC fraud-prevention headers: ${name}`);
    }
    out[name] = value;
  }
  return out;
}

/**
 * Collect the six and `POST` them, bound to the `state` `start_hmrc_consent` minted (§G5.3 step 2).
 *
 * It goes through `client` - the single fetch site in the bundle - which is what attaches the session
 * cookie (`credentials: "include"`) and the `X-CSRF-Token` header from `sessionSink` on a non-safe
 * method. **This module never reads the CSRF token itself**; going through `client.ts` is the whole
 * reason v7's design works, and a hand-rolled `fetch` here would reproduce v6's no-op guard.
 *
 * The caller follows `authorize_url` only after this resolves (§G5.3 step 3): a rejection here means no
 * attestation is stored, and the callback would 409 with `hmrc_fraud_headers_missing`.
 */
export async function submitFraudSignals(state: string, required: readonly string[]): Promise<void> {
  const values = collectFraudSignals(required);
  await unwrap(hmrcPaths.POST<void>("/api/v1/connections/hmrc/fraud-headers", { body: { state, values } }));
}

/** `start_hmrc_consent`'s response body (`api/schemas/hmrc.py::ConsentStart`). */
export interface ConsentStart {
  authorize_url: string;
  state: string;
  expires_at: string;
  required_headers: string[];
  notices: string[];
}

/**
 * Start the flow (§G5.3 step 1). `vrn` is REQUIRED (R-17.8): the callback creates the `connections` row and
 * `connections.org_id` is `NOT NULL`, while HMRC's token response carries no VRN - so it is the operator's
 * knowledge and this is where it enters the system.
 */
export async function startHmrcConsent(migrationId: string, vrn: string): Promise<ConsentStart> {
  return await unwrap(
    hmrcPaths.GET<ConsentStart>("/api/v1/connections/hmrc/consent", {
      params: { query: { migration_id: migrationId, vrn } },
    }),
  );
}
